import {
  FieldType,
  QueryBinding,
  QuerySpec,
  QueryWindow,
} from "@src/types/dashboards";
import { DEFAULT_POLLING_INTERVAL, isoToDurationMs } from "./constants";

export interface WidgetDataSource {
  group: string; // machine / source_id
  source: string; // OPC UA node id (address)
}

/** Read the machine + node identifiers from a widget's query. */
export const getWidgetDataSource = (
  query: QuerySpec | null | undefined,
): WidgetDataSource | null => {
  const binding = query?.bindings?.[0];
  if (!query?.source || !binding?.address) return null;
  return { group: query.source, source: binding.address };
};

/** Read the `window` block from a query, accepting the trailing-underscore
 *  variant for `from` / `to` (BE sometimes serializes back with the
 *  Python reserved-word workaround `from_` / `to_` — see dashboards-ticket-
 *  list §data-window-aliases). Both keys are interchangeable from the FE's
 *  perspective; canonical (`from`, `to`) wins when both are present.
 *  Caller checks `from ?? from_` for symmetry with the existing types.
 *  ponytail: defensive shim, fix the BE serialization when consolidated.
 */
export const getWidgetWindow = (
  query: QuerySpec | null | undefined,
):
  | { duration?: string; from?: string; to?: string; size?: number }
  | undefined => {
  const w = query?.window as
    | {
        duration?: string;
        from?: string;
        from_?: string;
        to?: string;
        to_?: string;
        size?: number;
      }
    | undefined;
  if (!w) return undefined;
  return {
    duration: w.duration,
    from: w.from ?? w.from_,
    to: w.to ?? w.to_,
    // Event-count window (`size`), read by events widgets only. Every other
    // caller ignores it — the key has to be carried through here or a save
    // would silently drop it.
    size: w.size,
  };
};

/** The parameters for one events request. The service accepts a count, a time
 *  window, or both together — but our range control offers one mode at a time,
 *  so only that mode's parameters are sent. Combining them is a product
 *  decision we have not taken, not a limit of the endpoint.
 *  A stored `duration` is relative, so it becomes an absolute pair against
 *  `nowMs` at request time. */
export const eventsWindowParams = (
  window: ReturnType<typeof getWidgetWindow>,
  nowMs: number,
): { size?: number; from?: string; to?: string } => {
  if (window?.size != null) {
    return { size: window.size };
  }
  const durationMs = isoToDurationMs(window?.duration);
  if (durationMs != null) {
    return {
      from: new Date(nowMs - durationMs).toISOString(),
      to: new Date(nowMs).toISOString(),
    };
  }
  return { from: window?.from, to: window?.to };
};

/** Build the query spec for a single-value gauge binding. */
/** A gauge bound (min / max / target) driven by an OPC-UA node instead of a
 *  static number in `semantics`. */
export interface GaugeBoundNode {
  field: "min" | "max" | "target";
  address: string;
}

export const buildWidgetQuery = (
  machineId: string,
  nodeId: string,
  pollingInterval?: string,
  /** Gauge only: the bounds a node drives. Appended AFTER the value binding —
   *  the fetch layer reads `bindings[0]` as the widget's own value. */
  boundNodes: GaugeBoundNode[] = [],
): QuerySpec => ({
  source: machineId,
  shape: "scalar",
  fields: {
    value: "float",
    // Declared here as well as bound below: `fields` is the query contract's
    // field -> type map, and every bound field must be in it.
    ...Object.fromEntries(
      boundNodes.map((bound) => [bound.field, "float" as const]),
    ),
  },
  sampling: {
    frequency: "raw",
    polling_interval: pollingInterval ?? DEFAULT_POLLING_INTERVAL,
  },
  bindings: [
    { field: "value", source: machineId, address: nodeId },
    ...boundNodes.map((bound) => ({
      field: bound.field,
      source: machineId,
      address: bound.address,
    })),
  ],
});

/** Build the query spec for a trend-line (timeseries) binding. */
export const buildTrendLineQuery = (options: {
  machineId: string;
  nodeId: string;
  frequency?: string | null;
  /** Polling cadence in raw mode (e.g. "30s"). Aggregated ignores it — the
   *  fetch layer derives the cadence from the bucket size. */
  pollingInterval?: string | null;
  /** Relative window (rolling at render): ISO-8601, e.g. "PT1H". */
  durationIso?: string | null;
  /** Absolute window (fixed dates): ISO timestamp pair. */
  from?: string | null;
  to?: string | null;
}): QuerySpec => {
  const window = options.durationIso
    ? { duration: options.durationIso }
    : options.from || options.to
    ? { from: options.from ?? undefined, to: options.to ?? undefined }
    : undefined;
  return {
    source: options.machineId,
    shape: "timeseries",
    fields: { value: "float" },
    sampling: {
      frequency: options.frequency ?? "raw",
      polling_interval: options.pollingInterval ?? DEFAULT_POLLING_INTERVAL,
    },
    ...(window ? { window } : {}),
    bindings: [
      {
        field: "value",
        source: options.machineId,
        address: options.nodeId,
      },
    ],
  };
};

/** Build the query spec for a shot-data line chart (events source). The
 *  `value_0..N` bindings are filled in once the user has picked fields from
 *  the event type's JSON tree; a freshly created widget carries none yet. */
export const buildShotDataLineChartQuery = (options: {
  machineId: string;
  eventType: string;
  fields?: Record<string, FieldType>;
  window?: QueryWindow;
  bindings?: QueryBinding[];
  /** User-picked polling cadence from the editor; falls back to the default
   *  for freshly-seeded widgets that have no editor snapshot yet. */
  pollingInterval?: string;
}): QuerySpec => ({
  source: options.machineId,
  shape: "timeseries",
  event_type: options.eventType,
  fields: options.fields ?? {},
  // Events are raw readings — one point per event, never aggregated.
  sampling: {
    frequency: "raw",
    polling_interval: options.pollingInterval ?? DEFAULT_POLLING_INTERVAL,
  },
  ...(options.window ? { window: options.window } : {}),
  bindings: options.bindings ?? [],
});

/** Build the query spec for a multi-line (multi-binding timeseries) widget.
 *  Each binding has its own `source` (machine) + `address` (node); the
 *  top-level `source` mirrors the first binding so legacy consumers reading
 *  `query.source` keep working. Frequency is ALWAYS aggregated (raw is not
 *  meaningful when timestamps must coincide across series). */
export const buildMultiLineQuery = (options: {
  bindings: QueryBinding[];
  /** Aggregated-only — caller passes the chosen granularity (e.g. "hourly").
   *  Defaults to "hourly" per spec §3. */
  frequency?: string | null;
  durationIso?: string | null;
  from?: string | null;
  to?: string | null;
}): QuerySpec => {
  const window = options.durationIso
    ? { duration: options.durationIso }
    : options.from || options.to
    ? { from: options.from ?? undefined, to: options.to ?? undefined }
    : undefined;
  const firstSource = options.bindings[0]?.source ?? "";
  return {
    // Top-level source = first binding's machine (legacy readers); each
    // binding carries its own source/address for multi-machine queries.
    source: firstSource,
    shape: "timeseries",
    fields: { value: "float" },
    sampling: {
      frequency: options.frequency ?? "hourly",
      polling_interval: "30s",
    },
    ...(window ? { window } : {}),
    bindings: options.bindings,
  };
};
